import axiosInterceptor, { endSession } from './axiosInterceptor'
import { API_BASE_URL } from './constants'
import { getAuthToken } from 'utils/authHeaders'

/**
 * Ask MDT's transport (MYD-625).
 *
 * The session routes are plain JSON and go through the shared axios instance
 * like every other service. Sending a message is different: the answer streams
 * back as server-sent events, and axios cannot stream in a browser, so it uses
 * `fetch` and reads the body as it arrives. It still behaves like the axios
 * services where it matters:
 *
 *   - the same token, from the same place (`getAuthToken`)
 *   - a 401 ends the session exactly as `axiosInterceptor` does
 *   - an expired trial is sent to the conversion screen, as the global
 *     interceptor does for axios
 *
 * The plan endpoints (MYD-620) are plain JSON too: get, edit and execute.
 */

const api = () => axiosInterceptor(API_BASE_URL)

// =============================================================================
// Types
// =============================================================================

export type AiContextType = 'location' | 'project' | 'portfolio' | 'evaluation'

/** The screen the user is asking from. The server checks they can open it. */
export interface AiRouteContext {
	type: AiContextType
	id: string
}

export interface AiSessionSummary {
	id: string
	title: string
	routeContext: AiRouteContext | null
	createdAt: string
	updatedAt: string
}

export interface AiTranscriptEntry {
	role: 'user' | 'assistant'
	text: string
}

export interface AiSession extends AiSessionSummary {
	messages: AiTranscriptEntry[]
}

export type AiToolStatus = 'running' | 'done' | 'error'

// --- Plans (backend/ai/plan/schema.js, ai/tools/propose.js) -------------------

export type AiPlanOp =
	| 'location.create'
	| 'location.useExisting'
	| 'project.create'
	| 'asset.addPartner'
	| 'partner.inviteByEmail'
	| 'task.create'
	| 'task.applyTemplate'
	| 'evaluation.create'
	| 'evaluationSite.addFromLocation'
	| 'evaluationSite.addFromPlace'
	| 'explorerReport.createFromTemplate'
	| 'explorerReport.createFromDefinition'
	| 'presentation.createFromTemplate'
	| 'portfolio.addLocation'

export interface AiPlanCandidate {
	ref: string
	label: string
}

export interface AiPlanStep {
	id: string
	op: AiPlanOp
	args: Record<string, any>
	dependsOn: string[]
	needsInput: string[]
	candidates?: Record<string, AiPlanCandidate[]>
	label?: string
}

export interface AiPlanBody {
	summary: string
	steps: AiPlanStep[]
}

/** A resolved place, behind a `place` ref. */
export interface AiPlace {
	name: string
	brand: string
	address: string
	city: string
	stateProvince: string
	postalCode: string
	latitude: number
	longitude: number
	source: 'overture' | 'photon' | 'nominatim'
}

/** What a ref in the plan names: `refs[refId]`. */
export interface AiRefInfo {
	kind: string
	label: string
	/** An existing location found at a place: that place's ref. */
	near?: string
	place?: AiPlace
	/** What it holds, for the card: a task template's first task names (MYD-637). */
	items?: string[]
}

/** Why a plan cannot run yet. */
export interface AiPlanGap {
	step: string
	field: string
	reason: string
}

/** Why a plan, or an edit to it, is not valid. */
export interface AiPlanProblem {
	step: string | null
	path: string
	message: string
}

export type AiPlanStatus = 'proposed' | 'edited' | 'executing' | 'done' | 'partial' | 'failed' | 'undoing' | 'undone'

export interface AiStepResult {
	stepId: string
	status: 'done' | 'failed' | 'skipped'
	created?: { kind: string | null; id: string | null; label: string; url: string | null; reused: boolean }
	error?: string | null
	code?: string | null
	/** Done with a caveat, e.g. a deck the builder shortened (MYD-627). */
	warnings?: string[]
}

/** A plan as the endpoints and the `plan` event serve it. */
export interface AiPlanView {
	id: string
	sessionId: string
	status: AiPlanStatus
	plan: AiPlanBody
	results: AiStepResult[]
	undoUntil: string | null
	undoResults: AiUndoOutcome[]
	executable?: boolean
	blocking?: AiPlanGap[]
	problems?: AiPlanProblem[]
	refs?: Record<string, AiRefInfo>
}

/** One event from `POST /ai/sessions/:id/messages`, as the server sends it. */
export type AiStreamEvent =
	| { type: 'session'; id: string }
	| { type: 'text'; delta: string }
	| { type: 'tool'; id: string; name: string; status: AiToolStatus }
	| ({ type: 'plan' } & AiPlanView)
	| { type: 'done'; stopReason: string; intent: string; planId?: string }
	| { type: 'error'; message: string; code: string }

/** A refusal or failure, with what the client needs to say something useful. */
export class AiRequestError extends Error {
	status: number
	code: string | null
	retryAfter: number | null
	/** The rest of a refusal's body: a plan's `problems`, `blocking` or `errors`. */
	details: Record<string, any>

	constructor(
		message: string,
		{
			status = 0,
			code = null,
			retryAfter = null,
			details = {},
		}: { status?: number; code?: string | null; retryAfter?: number | null; details?: Record<string, any> } = {},
	) {
		super(message)
		this.name = 'AiRequestError'
		this.status = status
		this.code = code
		this.retryAfter = retryAfter
		this.details = details
	}
}

// =============================================================================
// Sessions (JSON)
// =============================================================================

export async function listSessions(): Promise<AiSessionSummary[]> {
	const { data } = await api().get<AiSessionSummary[]>('/ai/sessions')
	return data
}

export async function createSession(routeContext: AiRouteContext | null = null): Promise<AiSessionSummary> {
	const { data } = await api().post<AiSessionSummary>('/ai/sessions', routeContext ? { routeContext } : {})
	return data
}

export async function getSession(id: string): Promise<AiSession> {
	const { data } = await api().get<AiSession>(`/ai/sessions/${encodeURIComponent(id)}`)
	return data
}

// =============================================================================
// Plans (JSON)
// =============================================================================

/** An axios failure as an AiRequestError, keeping the refusal's details. */
const planRefusal = (error: any): AiRequestError => {
	const body = error?.response?.data ?? {}
	const { message, code, stack: _stack, ...details } = body
	return new AiRequestError(message || error?.message || 'Request failed', {
		status: error?.response?.status ?? 0,
		code: code ?? null,
		details,
	})
}

const planPath = (id: string, suffix = '') => `/ai/plans/${encodeURIComponent(id)}${suffix}`

export async function getPlan(id: string): Promise<AiPlanView> {
	try {
		const { data } = await api().get<AiPlanView>(planPath(id))
		return data
	} catch (error) {
		throw planRefusal(error)
	}
}

/** Save the user's edits. A 400 carries `details.problems`, per step. */
export async function updatePlan(id: string, plan: AiPlanBody): Promise<AiPlanView> {
	try {
		const { data } = await api().put<AiPlanView>(planPath(id), { plan })
		return data
	} catch (error) {
		throw planRefusal(error)
	}
}

/** Run a plan the user confirmed. A 409 carries `details.blocking` or `details.errors`. */
export async function executePlan(id: string): Promise<AiPlanView> {
	try {
		const { data } = await api().post<AiPlanView>(planPath(id, '/execute'))
		return data
	} catch (error) {
		throw planRefusal(error)
	}
}

/** What undo did with each step (backend/ai/undo.js). */
export interface AiUndoOutcome {
	stepId: string
	op?: string
	outcome: 'undone' | 'partial' | 'kept' | 'none' | 'failed'
	reason?: string
}

// --- Evaluation drafts (MYD-639) ------------------------------------------------

export type EvaluationDraftKind = 'comparison' | 'memo'

/** What a draft was written from, for the panel to show beside it. */
export interface EvaluationDraftSources {
	sites: string[]
	scoring: boolean
	financials: 'included' | 'none entered' | 'not on this plan'
	risks: number
	prosAndCons: number
}

export interface EvaluationDraft {
	kind: EvaluationDraftKind
	/** Markdown, for AskMdtMarkdown. Saved nowhere: the user copies it. */
	text: string
	sources: EvaluationDraftSources
	model: string
	generatedAt: string
}

/** Draft a site comparison (2 to 6 sites) or an approval memo for one of my evaluations. */
export async function draftEvaluation(evaluationId: string, input: { kind: EvaluationDraftKind; siteIds?: string[] }): Promise<EvaluationDraft> {
	const { data } = await api().post<EvaluationDraft>(`/ai/evaluations/${evaluationId}/draft`, input)
	return data
}

/** Undo a plan that ran, within 24 hours. A 410 means the window has passed. */
export async function undoPlan(id: string): Promise<{ status: AiPlanStatus; steps: AiUndoOutcome[] }> {
	try {
		const { data } = await api().post<{ status: AiPlanStatus; steps: AiUndoOutcome[] }>(planPath(id, '/undo'))
		return data
	} catch (error) {
		throw planRefusal(error)
	}
}

// =============================================================================
// Server-sent events
// =============================================================================

export interface SseMessage {
	event: string
	data: string
}

/**
 * An incremental SSE parser, per the spec's line rules. Feed it text as it
 * arrives, in chunks that can split anywhere, a line or a CRLF included; it
 * returns each event once its blank line has arrived.
 *
 *   - `data:` lines in one event are joined with "\n"
 *   - one leading space after the colon is dropped
 *   - `:` starts a comment (keep-alives); unknown fields are ignored
 *   - an event with no `event:` field is a `message`
 */
export const createSseParser = () => {
	let buffer = ''
	let event = ''
	let data: string[] = []

	const dispatch = (out: SseMessage[]) => {
		if (data.length) out.push({ event: event || 'message', data: data.join('\n') })
		event = ''
		data = []
	}

	const line = (raw: string, out: SseMessage[]) => {
		if (raw === '') return dispatch(out)
		if (raw.startsWith(':')) return
		const colon = raw.indexOf(':')
		const field = colon === -1 ? raw : raw.slice(0, colon)
		let value = colon === -1 ? '' : raw.slice(colon + 1)
		if (value.startsWith(' ')) value = value.slice(1)
		if (field === 'event') event = value
		else if (field === 'data') data.push(value)
	}

	return {
		push(chunk: string): SseMessage[] {
			const out: SseMessage[] = []
			buffer += chunk
			// A trailing CR may be half of a CRLF still in flight, so it waits.
			const match = /\r\n|\r(?!$)|\n/g
			let start = 0
			let found: RegExpExecArray | null
			while ((found = match.exec(buffer))) {
				line(buffer.slice(start, found.index), out)
				start = found.index + found[0].length
			}
			buffer = buffer.slice(start)
			return out
		},
		/** The stream ended: an event still waiting for its blank line is dispatched. */
		end(): SseMessage[] {
			const out: SseMessage[] = []
			if (buffer) line(buffer.replace(/\r$/, ''), out)
			buffer = ''
			dispatch(out)
			return out
		},
	}
}

/** One parsed SSE message as a typed event, or null for one this client does not know. */
const toEvent = (message: SseMessage): AiStreamEvent | null => {
	try {
		const payload = JSON.parse(message.data)
		if (!['session', 'text', 'tool', 'plan', 'done', 'error'].includes(message.event)) return null
		return { type: message.event, ...payload } as AiStreamEvent
	} catch {
		return null
	}
}

// =============================================================================
// Sending a message (SSE over fetch)
// =============================================================================

/** The refusal body, when the server sent JSON, else what the status says. */
const refusalFrom = async (response: Response): Promise<AiRequestError> => {
	let body: { message?: string; code?: string; retryAfter?: number } = {}
	try {
		body = await response.json()
	} catch {
		/* Not JSON: a proxy error page, say. The status still says enough. */
	}
	const header = Number(response.headers.get('Retry-After'))
	return new AiRequestError(body.message || `Request failed with status ${response.status}`, {
		status: response.status,
		code: body.code ?? null,
		retryAfter: Number.isFinite(body.retryAfter) ? Number(body.retryAfter) : Number.isFinite(header) && header > 0 ? header : null,
	})
}

const CONVERT_PATH = '/convert'

/**
 * Ask, and receive the answer as it is written: an async iterator of events,
 * ending with `done` or `error`. Throws `AiRequestError` when the server
 * refuses before streaming (400, 401, 403, 404, 409, 429, 503), and an
 * `AbortError` when `signal` aborts.
 */
export async function* sendMessage(
	sessionId: string,
	input: { text: string; routeContext?: AiRouteContext | null },
	{ signal, fetchImpl = fetch }: { signal?: AbortSignal; fetchImpl?: typeof fetch } = {},
): AsyncGenerator<AiStreamEvent> {
	const token = getAuthToken()
	const response = await fetchImpl(`${API_BASE_URL}/ai/sessions/${encodeURIComponent(sessionId)}/messages`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'text/event-stream',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		body: JSON.stringify(input.routeContext === undefined ? { text: input.text } : input),
		signal,
	})

	if (!response.ok) {
		const refusal = await refusalFrom(response)
		// The same endings the axios services get (axiosInterceptor, setupTrialInterceptor).
		if (refusal.status === 401) endSession()
		if (refusal.code === 'TRIAL_EXPIRED' && !window.location.pathname.startsWith(CONVERT_PATH)) window.location.assign(CONVERT_PATH)
		throw refusal
	}
	if (!response.body) throw new AiRequestError('The answer could not be read.', { status: response.status })

	const reader = response.body.getReader()
	const decoder = new TextDecoder()
	const parser = createSseParser()
	try {
		while (true) {
			const { done, value } = await reader.read()
			const messages = done ? [...parser.push(decoder.decode()), ...parser.end()] : parser.push(decoder.decode(value, { stream: true }))
			for (const message of messages) {
				const event = toEvent(message)
				if (event) yield event
			}
			if (done) return
		}
	} finally {
		// Stopping early (an abort, or the consumer breaking out) releases the
		// connection rather than reading an answer nobody will see.
		reader.cancel().catch(() => {})
	}
}

// =============================================================================
// Words for the user
// =============================================================================

const inSeconds = (seconds: number) => (seconds < 60 ? `${seconds} second${seconds === 1 ? '' : 's'}` : `${Math.ceil(seconds / 60)} minutes`)

/**
 * What to tell the user when Ask MDT refuses or fails. The server's own words
 * are used where they are written for a person (the budget messages); the rest
 * are mapped by code, so a status line never reaches the screen.
 */
export const friendlyAiError = (error: unknown): string => {
	if (!(error instanceof AiRequestError)) return 'Ask MDT could not be reached. Check your connection and try again.'
	switch (error.code) {
		case 'AI_RATE_LIMITED':
			return error.retryAfter ? `You're sending messages quickly. Try again in ${inSeconds(error.retryAfter)}.` : "You're sending messages quickly. Try again in a moment."
		case 'AI_BUDGET_EXCEEDED':
			return error.message || "Ask MDT's allowance has been used up for now."
		case 'AI_LIMITS_UNAVAILABLE':
		case 'AI_AGENT_FAILED':
			return 'Ask MDT is unavailable right now. Try again in a moment.'
		case 'AI_SESSION_BUSY':
			return 'Ask MDT is still answering your last message.'
		case 'AI_NOT_ALLOWED':
			return 'Ask MDT is not available on your account.'
		default:
	}
	if (error.status === 404) return 'That conversation is no longer available. Start a new one.'
	if (error.status === 400) return error.message
	return 'Ask MDT could not answer just now. Try again in a moment.'
}

// --- Slide drafts (MYD-640) -----------------------------------------------------

export type SlideDraftTarget = 'notes' | 'text'

/** How many of the slide's blocks the draft was written from, and how many the user may not see. */
export interface SlideDraftBasis {
	blocks: number
	unavailable: number
}

export interface SlideDraft {
	target: SlideDraftTarget
	/** notes: plain text. text: the deck's markdown subset, as a user would type it. */
	text: string
	basedOn: SlideDraftBasis
	model: string
	generatedAt: string
}

export interface SlideDraftInput {
	target: SlideDraftTarget
	/** The slide as it is in the editor, unsaved edits included. */
	slide: { title: string; subtitle: string; speakerNotes: string; blocks: unknown[] }
	anchor: { scope: string; entityId: string } | null
	storyTitle: string
	/** text: the generic.text block to write */
	blockId?: string
	/** A one-line ask, e.g. "shorter" */
	instruction?: string
}

/** Draft speaker notes or a text block's text for a slide of one of my decks. */
export async function draftSlide(presentationId: string, input: SlideDraftInput): Promise<SlideDraft> {
	const { data } = await api().post<SlideDraft>(`/ai/presentations/${presentationId}/draft`, input)
	return data
}

// --- Score suggestions (MYD-641) ------------------------------------------------

export type ScoreConfidence = 'low' | 'medium' | 'high'

/** A fact a suggestion rests on, as the model was given it. */
export interface ScoreEvidence {
	id: string
	kind: 'site' | 'demographics' | 'retailer' | 'proCon' | 'risk' | 'financials'
	label: string
	text: string
}

export interface ScoreSuggestion {
	criterionId: string
	criterion: string
	weight: number
	/** The site's current average on this criterion, or null if unscored. */
	currentScore: number | null
	/** 1-10 in halves, or null when there was not enough evidence. */
	score: number | null
	confidence: ScoreConfidence | null
	rationale: string
	evidence: ScoreEvidence[]
}

export interface ScoreSuggestions {
	siteId: string
	siteName: string
	suggestions: ScoreSuggestion[]
	basedOn: { siteFacts: number; demographics: number; retailers: number; prosAndCons: number; risks: number; financials: 'included' | 'none entered' | 'not on this plan' }
	model: string
	generatedAt: string
}

/** Suggest a score per criterion for one site of my evaluation. Writes nothing. */
export async function suggestScores(evaluationId: string, siteId: string): Promise<ScoreSuggestions> {
	const { data } = await api().post<ScoreSuggestions>(`/ai/evaluations/${evaluationId}/score-suggestions`, { siteId })
	return data
}

// --- Area summary (MYD-646) -----------------------------------------------------

export type AreaAnchorType = 'Location' | 'EvaluationSite'

/** One sentence and the facts it cites. The server's own "no retailers" line cites none. */
export interface AreaSentence {
	text: string
	evidence: ScoreEvidence[]
}

export interface AreaNarrative {
	anchorType: AreaAnchorType
	anchorId: string
	name: string
	paragraphs: { sentences: AreaSentence[] }[]
	/** Plain paragraphs, a blank line between them: what Copy copies. Saved nowhere. */
	text: string
	basedOn: { demographics: number; retailers: number; cotenants: number; competitors: number }
	model: string
	generatedAt: string
}

/** Write the area summary for a location I can open or an evaluation site of mine. Writes nothing. */
export async function draftAreaNarrative(anchorType: AreaAnchorType, anchorId: string): Promise<AreaNarrative> {
	const { data } = await api().post<AreaNarrative>('/ai/area-narrative', { anchorType, anchorId })
	return data
}

// --- Chat summary (MYD-650) -----------------------------------------------------

/** A message a summary entry rests on. */
export interface ChatSummaryEvidence {
	id: string
	messageId: string
	sender: string
	/** ISO time the message was sent. */
	at: string
	text: string
}

export interface ChatSummaryEntry {
	text: string
	evidence: ChatSummaryEvidence[]
}

export interface ChatSummary {
	key: string
	kind: 'thread' | 'project' | 'portfolio'
	/** `unread`: from where I last read a thread. `recent`: the latest messages. */
	window: { kind: 'unread' | 'recent'; count: number; from: string; to: string }
	overview: ChatSummaryEntry[]
	decisions: ChatSummaryEntry[]
	openQuestions: ChatSummaryEntry[]
	/** `owner` is someone in the conversation, or '' when it is not clear who. */
	actionItems: (ChatSummaryEntry & { owner: string })[]
	/** The summary as plain text: what Copy copies. Saved nowhere. */
	text: string
	model: string
	generatedAt: string
}

/**
 * Summarise a conversation I am in: a DM thread id, a project id, or
 * `portfolio:<id>`. Sends its messages to the model; writes nothing.
 */
export async function summariseChat(key: string): Promise<ChatSummary> {
	const { data } = await api().post<ChatSummary>('/ai/chats/summary', { key })
	return data
}

// --- Risk suggestions (MYD-647) -------------------------------------------------

export interface RiskSuggestion {
	name: string
	/** One of the response's `categories`, "Other" when nothing fitted. */
	category: string
	/** Whole numbers, 1-5. */
	likelihood: number
	impact: number
	mitigation: string
	rationale: string
	evidence: ScoreEvidence[]
}

export interface RiskSuggestions {
	siteId: string
	siteName: string
	/** At most eight, none repeating a risk the site already has. */
	suggestions: RiskSuggestion[]
	/** The categories the model chose from: this evaluation's, the risk templates', then "Other". */
	categories: string[]
	basedOn: { facts: number; siteFacts: number; demographics: number; retailers: number; prosAndCons: number; existingRisks: number; financials: 'included' | 'none entered' | 'not on this plan' }
	model: string
	generatedAt: string
}

/** Suggest new risks for one site of my evaluation. Writes nothing. */
export async function suggestRisks(evaluationId: string, siteId: string): Promise<RiskSuggestions> {
	const { data } = await api().post<RiskSuggestions>(`/ai/evaluations/${evaluationId}/risk-suggestions`, { siteId })
	return data
}

// --- Tasks from a note (MYD-643) --------------------------------------------------

/** A project to choose, when a location note could go on several. */
export interface NoteTaskProject {
	id: string
	name: string
}

/**
 * Propose tasks from a location note: a PROPOSED plan, for the plan card.
 * Nothing is created until the user confirms it there.
 *
 * A 409 `CHOOSE_PROJECT` carries `projects`; ask again with one of their ids.
 */
export async function proposeNoteTasks(noteId: string, projectId?: string): Promise<AiPlanView> {
	const { data } = await api().post<AiPlanView>(`/ai/notes/location/${noteId}/tasks`, projectId ? { projectId } : {})
	return data
}
